import type { TranslocoService } from '@jsverse/transloco';

interface ApiErrorBody {
	code?: unknown;
	params?: unknown;
}

function errorBody(err: unknown): ApiErrorBody | null {
	if (!err || typeof err !== 'object') return null;
	// HttpErrorResponse carries the API body in `error`; errors rethrown by services carry `code` themselves
	const nested = (err as { error?: unknown }).error;
	if (nested && typeof nested === 'object' && 'code' in nested) return nested as ApiErrorBody;
	return 'code' in err ? (err as ApiErrorBody) : null;
}

/** Stable error code the backend sent with the error (e.g. 'EMAIL_ALREADY_REGISTERED'), if any. */
export function apiErrorCode(err: unknown): string | undefined {
	const code = errorBody(err)?.code;
	return typeof code === 'string' && code ? code : undefined;
}

/**
 * Text to show for a failed API call: `errors.<code>` translated with the error's params when the backend
 * sent a known code, otherwise the screen's generic `fallbackKey`. Never the raw backend message: it is
 * English, not written for users, and may change without notice.
 */
export function translateApiError(
	err: unknown,
	transloco: TranslocoService,
	fallbackKey: string,
	fallbackParams: Record<string, unknown> = {},
): string {
	const code = apiErrorCode(err);
	if (code) {
		const key = `errors.${code}`;
		const params = errorBody(err)?.params;
		const text = transloco.translate(
			key,
			params && typeof params === 'object' ? (params as Record<string, unknown>) : {},
		);
		if (text && text !== key) return text;
	}
	return transloco.translate(fallbackKey, fallbackParams);
}
